import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Typography, Button, Skeleton, Fab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import styles from './Offers.module.scss';
import { PageLayout } from 'lib';
import { OffersAPI } from 'api_inbusiness';
import { useUser } from 'hooks/useUser';
import OfferCard from './OfferCard/OfferCard';
import CreateOfferModal from './CreateOfferModal';
import EditOfferModal from './EditOfferModal';
import DeleteOfferModal from './DeleteOfferModal';
import { toast } from 'react-toastify';

const Offers = () => {
  const { user } = useUser();
  const history = useHistory();
  
  const [offers, setOffers] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessChecked, setAccessChecked] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Check access on mount
  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const res = await OffersAPI.checkAccess();
    if (res.ok && res.data.hasAccess) {
      setHasAccess(true);
      loadOffers();
    } else {
      setHasAccess(false);
      setLoading(false);
    }
    setAccessChecked(true);
  };

  const loadOffers = async () => {
    setLoading(true);
    const res = await OffersAPI.getAll();
    if (res.ok) {
      setOffers(res.data);
    }
    setLoading(false);
  };

  const handleOfferCreated = () => {
    setCreateModalOpen(false);
    loadOffers();
  };

  const handleEditOffer = (offer) => {
    setSelectedOffer(offer);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setSelectedOffer(null);
    loadOffers();
  };

  const handleDeleteOffer = (offer) => {
    setSelectedOffer(offer);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setDeleteModalOpen(false);
    setSelectedOffer(null);
    loadOffers();
  };

  // Access Denied View - CTA for non-members
  if (accessChecked && !hasAccess) {
    return (
      <PageLayout title="Oferte Exclusive" hasBackButton={false}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedContent}>
            <div className={styles.lockIconWrapper}>
              <LockIcon className={styles.lockIcon} />
            </div>
            <Typography variant="h4" className={styles.accessTitle}>
              Accesează Ofertele Exclusive InBusiness
            </Typography>
            <Typography variant="body1" className={styles.accessDescription}>
              Membrii activi au acces la oferte și reduceri exclusive de la partenerii din comunitatea noastră.
            </Typography>
            
            <div className={styles.benefitsList}>
              <div className={styles.benefitItem}>
                <span className={styles.checkMark}>✓</span>
                <span>Acces la toate ofertele și reducerile</span>
              </div>
              <div className={styles.benefitItem}>
                <span className={styles.checkMark}>✓</span>
                <span>Networking cu antreprenori de succes</span>
              </div>
              <div className={styles.benefitItem}>
                <span className={styles.checkMark}>✓</span>
                <span>Participare la evenimente exclusive</span>
              </div>
            </div>

            <Button
              variant="contained"
              size="large"
              className={styles.ctaButton}
              onClick={() => history.push('/becomeMember')}
            >
              🚀 Devino Membru Acum
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Oferte Exclusive" 
      hasBackButton={false}
      actionArea={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          className={styles.addButton}
        >
          Adaugă Ofertă
        </Button>
      }
    >
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.headerSection}>
          <LocalOfferIcon className={styles.headerIcon} />
          <div className={styles.headerText}>
            <Typography variant="body1" className={styles.headerSubtitle}>
              Reduceri și beneficii exclusive de la membrii comunității InBusiness
            </Typography>
          </div>
        </div>

        {/* Offers Grid */}
        {loading ? (
          <div className={styles.offersGrid}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={styles.skeletonCard}>
                <Skeleton variant="rectangular" height={160} />
                <Skeleton variant="text" height={32} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="60%" />
              </div>
            ))}
          </div>
        ) : offers.length === 0 ? (
          <div className={styles.emptyState}>
            <LocalOfferIcon className={styles.emptyIcon} />
            <Typography variant="h6">Nu există oferte momentan</Typography>
            <Typography variant="body2" color="textSecondary">
              Fii primul care adaugă o ofertă pentru comunitate!
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
              sx={{ mt: 2 }}
            >
              Adaugă Prima Ofertă
            </Button>
          </div>
        ) : (
          <div className={styles.offersGrid}>
            {offers.map((offer) => (
              <OfferCard 
                key={offer.id} 
                offer={offer} 
                currentUser={user}
                onClick={() => history.push(`/offers/${offer.id}`)}
                onEdit={handleEditOffer}
                onDelete={handleDeleteOffer}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button for Mobile */}
      <Fab 
        color="primary" 
        className={styles.fab}
        onClick={() => setCreateModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Offer Modal */}
      <CreateOfferModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleOfferCreated}
      />

      {/* Edit Offer Modal */}
      <EditOfferModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedOffer(null);
        }}
        onSuccess={handleEditSuccess}
        offer={selectedOffer}
      />

      {/* Delete Offer Modal */}
      <DeleteOfferModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedOffer(null);
        }}
        onSuccess={handleDeleteSuccess}
        offer={selectedOffer}
      />
    </PageLayout>
  );
};

export default Offers;

